import React from 'react';
import { object, string, func, bool } from 'prop-types';
import classNames from 'classnames';

// Import shared components
import { VideoFromFile, VideoPlayer, IconSpinner } from '../../components';
import { FormattedMessage } from '../../util/reactIntl';
import { propTypes } from '../../util/types';

// Import modules from this directory
import { ControlMenuVideo as ControlMenu } from './ControlMenu';
import css from './ListingVideo.module.css';

const isMatchingVideo = (video, videoId) => {
  return videoId === video?.id;
};

const ListingVideo = props => {
  const {
    className,
    video,
    isMenuOpen,
    onToggleMenu,
    onRemoveVideo,
    onPromoteVideo,
    promoteVideoInProgress,
    promoteVideoError,
    listingCoverVideoId,
    aspectWidth = 1,
    aspectHeight = 1,
  } = props;

  if (video.file && !video.src) {
    // Add control menu only when the video has been uploaded and can be removed
    const controlMenu = video.videoId ? (
      <ControlMenu
        video={video}
        isMenuOpen={isMenuOpen}
        onToggleMenu={onToggleMenu}
        onRemoveVideo={onRemoveVideo}
      />
    ) : null;

    // While video is uploading we show overlay on top of thumbnail
    const uploadingOverlay = !video.videoId ? (
      <div className={css.thumbnailLoading}>
        <IconSpinner className={css.spinnerIcon} />
      </div>
    ) : null;

    return (
      <VideoFromFile
        id={video.id}
        className={className}
        file={video.file}
        aspectWidth={aspectWidth}
        aspectHeight={aspectHeight}
      >
        {controlMenu}
        {uploadingOverlay}
      </VideoFromFile>
    );
  } else {
    const classes = classNames(css.root, className);
    
    const isCoverVideo = isMatchingVideo(video, listingCoverVideoId);

    const coverVideoLabel = isCoverVideo ? (
      <span className={classNames(css.imageLabel, css.imageLabelCover)}>
        <FormattedMessage id="EditListingGalleryPanel.coverVideoText" />
      </span>
    ) : null;

    // Handle promoting or unsetting a video as cover.
    const handlePromoteVideo = videoType => {
      let videoId = null;

      if (videoType === 'cover') {
        // If the video is already a cover, unset it as a cover by passing null.
        videoId = isCoverVideo ? null : video.id;
      }

      onPromoteVideo(videoId, videoType);
    };

    const videoLabels = isCoverVideo ? (
      <span className={css.imageLabels}>
        {coverVideoLabel}
      </span>
    ) : null;

    // This is shown when video is uploaded,
    // but the new responsive video is not yet downloaded by the browser.
    // This is absolutely positioned behind the actual video.
    const fallbackWhileDownloading = video.file ? (
      <VideoFromFile
        id={video.id}
        rootClassName={css.fallbackWhileDownloading}
        file={video.file}
        aspectWidth={aspectWidth}
        aspectHeight={aspectHeight}
      >
        <div className={css.thumbnailLoading}>
          <IconSpinner className={css.spinnerIcon} />
        </div>
      </VideoFromFile>
    ) : null;

    return (
      <div className={classes}>
        <div className={css.wrapper}>
          {fallbackWhileDownloading}
          {videoLabels}
          <VideoPlayer
            width={aspectWidth}
            height={aspectHeight}
            video={video}
            settings={{ playerTime: false, hoverPlay: false }}
          />
          <ControlMenu
            video={video}
            isMenuOpen={isMenuOpen}
            onToggleMenu={onToggleMenu}
            onRemoveVideo={onRemoveVideo}
            onPromoteVideo={videoType => handlePromoteVideo(videoType)}
            promoteVideoInProgress={promoteVideoInProgress}
            promoteVideoError={promoteVideoError}
            showCoverButton={!video.file} // display only for attached videos
            isCoverVideo={isCoverVideo}
          />
        </div>
      </div>
    );
  }
};

ListingVideo.defaultProps = {
  className: null,
  promoteVideoInProgress: false,
  promoteVideoError: null,
  listingCoverVideoId: null,
};

ListingVideo.propTypes = {
  className: string,
  video: object.isRequired,
  onRemoveVideo: func.isRequired,
  onPromoteVideo: func.isRequired,
  promoteVideoInProgress: bool.isRequired,
  promoteVideoError: propTypes.error,
  listingCoverVideoId: string,
};

export default ListingVideo;
